import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
} from '@mui/material';

import { setSidebarShow } from '../store/sidebarSlice';
import { SECTION_ICONS, activeSection, matchesEntry, visibleSections } from '../navigation';

const SIDEBAR_WIDTH = 256;

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    component="h2"
    sx={{
      px: 3,
      pt: 3,
      pb: 1,
      display: 'block',
      color: 'text.secondary',
      fontWeight: 600,
      letterSpacing: '0.08em',
    }}
  >
    {children}
  </Typography>
);

const NavItem = ({ icon: Icon, label, active, onClick }) => {
  const theme = useTheme();
  return (
    <ListItemButton
      onClick={onClick}
      selected={active}
      aria-current={active ? 'page' : undefined}
      sx={{
        mx: 1.5,
        mb: 0.5,
        borderRadius: 2,
        py: 1,
        transition: 'background-color 0.15s ease, color 0.15s ease',
        ...(active && {
          background: alpha(theme.palette.primary.main, 0.12),
          '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
          '& .MuiListItemText-primary': {
            color: theme.palette.primary.main,
            fontWeight: 600,
          },
        }),
        '&:hover': { background: alpha(theme.palette.primary.main, 0.08) },
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
      }}
    >
      {Icon && (
        <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
          <Icon fontSize="small" />
        </ListItemIcon>
      )}
      <ListItemText
        primary={label}
        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
      />
    </ListItemButton>
  );
};

/**
 * The second level of the navigation.
 *
 * On desktop it shows the items of whichever section the header has selected,
 * so the two menus never list the same thing twice. On mobile the header has no
 * room for the sections, so the drawer carries the whole tree instead.
 */
const AppSidebar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { sidebarShow } = useSelector((state) => state.sidebar);
  const { permissions = [] } = useSelector((state) => state.auth);

  const sections = React.useMemo(() => visibleSections(permissions), [permissions]);
  const current = activeSection(sections, location.pathname);

  // The store starts the drawer open, which suits the desktop's persistent
  // drawer but not the mobile one: that variant covers the whole screen, so a
  // phone would land on the navigation instead of the page it asked for.
  React.useEffect(() => {
    if (isMobile) dispatch(setSidebarShow(false));
  }, [isMobile, dispatch]);

  const go = (path) => {
    navigate(path);
    if (isMobile) dispatch(setSidebarShow(false));
  };

  // Desktop: a section with no items (Dashboard) has nothing to show, so the
  // drawer stays out of the way and the page takes the width.
  if (!isMobile && !current?.items.length) return null;

  const content = isMobile ? (
    <Box sx={{ pt: 2, pb: 2 }}>
      <Box sx={{ px: 3, pb: 2 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          PMS
        </Typography>
      </Box>
      <Divider sx={{ opacity: 0.1 }} />
      <List sx={{ pt: 1 }}>
        {sections.map((section) => {
          const SectionIcon = SECTION_ICONS[section.id];
          // A section with no items is a destination in its own right.
          if (!section.items.length) {
            return (
              <NavItem
                key={section.id}
                icon={SectionIcon}
                label={section.label}
                active={location.pathname.startsWith(section.path)}
                onClick={() => go(section.path)}
              />
            );
          }
          return (
            <React.Fragment key={section.id}>
              <SectionLabel>{section.label}</SectionLabel>
              {section.items.map((item) => (
                <NavItem
                  key={item.path}
                  icon={item.icon}
                  label={item.label}
                  active={matchesEntry(item, location.pathname)}
                  onClick={() => go(item.path)}
                />
              ))}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  ) : (
    <Box sx={{ pt: 1, pb: 2 }}>
      <SectionLabel>{current.label}</SectionLabel>
      <List sx={{ pt: 0 }}>
        {current.items.map((item) => (
          <NavItem
            key={item.path}
            icon={item.icon}
            label={item.label}
            active={matchesEntry(item, location.pathname)}
            onClick={() => go(item.path)}
          />
        ))}
      </List>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={sidebarShow}
      onClose={() => dispatch(setSidebarShow(false))}
      aria-label={isMobile ? 'Navigation' : `${current.label} menu`}
      sx={{
        width: sidebarShow && !isMobile ? SIDEBAR_WIDTH : 0,
        flexShrink: 0,
        // No width transition here: the Drawer already slides its paper with a
        // transform, and animating the wrapper's width on top of that thrashes
        // layout for every row of the page behind it.
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          top: { xs: 0, md: 64 },
          height: { xs: '100%', md: 'calc(100% - 64px)' },
        },
      }}
    >
      {content}
    </Drawer>
  );
};

export default React.memo(AppSidebar);
